import { useState, useMemo, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import ProductCard from "./components/ProductCard";
import ShopFilters from "./components/ShopFilters";
import ShopSidebar from "./components/ShopSidebar";
import Pagination from "./components/Pagination";
import CompareBar from "./components/CompareBar";
import CompareModal from "./components/CompareModal";
import ClearanceCorner from "./components/ClearanceCorner";
import RecentlyViewedStrip from "./components/RecentlyViewedStrip";
import { useShopifyProducts } from "../../context/ShopifyProductsContext";

type Category = "all" | "solar" | "gas" | "whole-home";
type SortOption = "featured" | "best-seller" | "price-asc" | "price-desc" | "newest" | "rating" | "sale" | "limited-stock";
type PriceBucket = "all" | "under500" | "500to1000" | "1000to2500" | "2500to5000" | "over5000";
type ViewMode = "grid" | "list";

const ITEMS_PER_PAGE = 12;

function priceInBucket(price: number, bucket: PriceBucket): boolean {
  if (bucket === "all") return true;
  if (bucket === "under500") return price < 500;
  if (bucket === "500to1000") return price >= 500 && price <= 1000;
  if (bucket === "1000to2500") return price > 1000 && price <= 2500;
  if (bucket === "2500to5000") return price > 2500 && price <= 5000;
  if (bucket === "over5000") return price > 5000;
  return true;
}

export default function ShopPage() {
  const { products, loading, error } = useShopifyProducts();
  const [searchParams] = useSearchParams();

  const [category, setCategory] = useState<Category>(() => {
    const urlCat = searchParams.get("category") as Category | null;
    const valid: Category[] = ["all", "solar", "gas", "whole-home"];
    return urlCat && valid.includes(urlCat) ? urlCat : "all";
  });
  const [sort, setSort] = useState<SortOption>("featured");
  const [showInStock, setShowInStock] = useState(true);
  const [showOutOfStock, setShowOutOfStock] = useState(true);
  const [priceBucket, setPriceBucket] = useState<PriceBucket>(() => {
    const urlPrice = searchParams.get("price") as PriceBucket | null;
    const valid: PriceBucket[] = ["all", "under500", "500to1000", "1000to2500", "2500to5000", "over5000"];
    return urlPrice && valid.includes(urlPrice) ? urlPrice : "all";
  });
  const [selectedBrands, setSelectedBrands] = useState<string[]>(() => {
    const urlBrand = searchParams.get("brand");
    return urlBrand ? [urlBrand] : [];
  });
  const [searchQuery, setSearchQuery] = useState<string>(() => searchParams.get("q") || "");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [page, setPage] = useState(1);

  // Sync filters when URL params change (e.g. navigating from homepage quiz or brand cards)
  useEffect(() => {
    const urlBrand = searchParams.get("brand");
    const urlCat = searchParams.get("category") as Category | null;
    const urlPrice = searchParams.get("price") as PriceBucket | null;
    const urlQ = searchParams.get("q") || "";
    const validCat: Category[] = ["all", "solar", "gas", "whole-home"];
    const validPrice: PriceBucket[] = ["all", "under500", "500to1000", "1000to2500", "2500to5000", "over5000"];
    if (urlBrand) { setSelectedBrands([urlBrand]); setPage(1); }
    if (urlCat && validCat.includes(urlCat)) { setCategory(urlCat); setPage(1); }
    if (urlPrice && validPrice.includes(urlPrice)) { setPriceBucket(urlPrice); setPage(1); }
    setSearchQuery(urlQ);
    if (urlQ) setPage(1);
  }, [searchParams]);

  const inStockCount = products.filter((p) => p.inStock).length;
  const outOfStockCount = products.filter((p) => !p.inStock).length;

  // Derive unique brands from actual products
  const availableBrands = useMemo(() => {
    const brandSet = new Set(products.map((p) => p.brand));
    return Array.from(brandSet).sort();
  }, [products]);

  const brandPills = useMemo(() => {
    return availableBrands.slice(0, 10).map((name) => ({
      name,
      icon: "ri-plug-line",
    }));
  }, [availableBrands]);

  const filtered = useMemo(() => {
    const sq = searchQuery.trim().toLowerCase();
    let list = products.filter((p) => {
      if (category !== "all" && p.category !== category) return false;
      if (!showInStock && p.inStock) return false;
      if (!showOutOfStock && !p.inStock) return false;
      if (!priceInBucket(p.price, priceBucket)) return false;
      if (selectedBrands.length > 0 && !selectedBrands.includes(p.brand)) return false;
      if (sq.length > 0) {
        const haystack = [p.name, p.brand, p.category, ...(p.highlights || []), p.badge || ""]
          .join(" ")
          .toLowerCase();
        if (!sq.split(" ").every((word) => haystack.includes(word))) return false;
      }
      return true;
    });
    if (sort === "best-seller") list = [...list].sort((a, b) => {
      const aBS = a.badge === "Best Seller" ? 0 : 1;
      const bBS = b.badge === "Best Seller" ? 0 : 1;
      return aBS - bBS;
    });
    else if (sort === "newest") list = [...list].sort((a, b) => {
      const aNew = a.isNew ? 0 : 1;
      const bNew = b.isNew ? 0 : 1;
      return aNew - bNew;
    });
    else if (sort === "price-asc") list = [...list].sort((a, b) => a.price - b.price);
    else if (sort === "price-desc") list = [...list].sort((a, b) => b.price - a.price);
    else if (sort === "rating") list = [...list].sort((a, b) => b.rating - a.rating);
    else if (sort === "sale") list = list.filter((p) => !!p.originalPrice);
    else if (sort === "limited-stock") list = [...list].sort((a, b) => {
      const aLim = a.isLimitedStock ? 0 : 1;
      const bLim = b.isLimitedStock ? 0 : 1;
      return aLim - bLim;
    });
    return list;
  }, [products, category, sort, showInStock, showOutOfStock, priceBucket, selectedBrands, searchQuery]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  // On-sale stats (across ALL products, not just filtered — always relevant)
  const saleProducts = useMemo(() => products.filter((p) => !!p.originalPrice && p.originalPrice > p.price), [products]);
  const biggestDiscount = useMemo(() => {
    if (saleProducts.length === 0) return 0;
    return Math.round(Math.max(...saleProducts.map((p) => ((p.originalPrice! - p.price) / p.originalPrice!) * 100)));
  }, [saleProducts]);
  const limitedStockCount = useMemo(() => products.filter((p) => p.isLimitedStock && p.inStock).length, [products]);

  const handleAvailability = (inStock: boolean, outOfStock: boolean) => { setShowInStock(inStock); setShowOutOfStock(outOfStock); setPage(1); };
  const handleCategory = (c: Category) => { setCategory(c); setPage(1); };
  const handlePriceBucket = (b: PriceBucket) => { setPriceBucket(b); setPage(1); };
  const handleSort = (s: SortOption) => { setSort(s); setPage(1); };
  const handleBrands = (brands: string[]) => { setSelectedBrands(brands); setPage(1); };
  const handleBrandQuickFilter = (brand: string) => {
    setSelectedBrands([brand]);
    setPage(1);
    window.scrollTo({ top: 400, behavior: "smooth" });
  };

  // ── Structured data ──────────────────────────────────────────────────────
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What\u2019s the difference between a solar generator and a gas generator?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Solar generators (portable power stations) use batteries charged by solar panels or wall outlets. They\u2019re silent, emission-free, and safe for indoor use. Gas generators produce more wattage but require ventilation, fuel storage, and regular maintenance.",
        },
      },
      {
        "@type": "Question",
        name: "How long will a backup power station power my home?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "It depends on the unit\u2019s capacity and your load. A 2kWh station can run a refrigerator for ~24 hours, lights for 2\u20133 days, or a CPAP machine for multiple nights. Use our Power Calculator on the homepage to estimate your needs.",
        },
      },
      {
        "@type": "Question",
        name: "Do you offer financing?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes \u2014 we offer 0% APR financing on qualifying orders through our approved lending partners. Apply in minutes and get a same-day decision.",
        },
      },
      {
        "@type": "Question",
        name: "What warranty coverage is included?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "All products include the full manufacturer warranty \u2014 typically 2\u20135 years for portable stations and up to 7 years for whole-home systems. We also assist with warranty claims on your behalf.",
        },
      },
    ],
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://ironhavensupply.com/" },
      { "@type": "ListItem", position: 2, name: "Shop Backup Power", item: "https://ironhavensupply.com/shop" },
    ],
  };

  // Dynamic title/desc vary by active category filter
  const categoryMeta: Record<string, { title: string; desc: string }> = {
    all: {
      title: "Shop Backup Power Systems & Solar Generators | Iron Haven Supply",
      desc: "Browse our full catalog of solar generators, battery backups, gas generators, and whole-home standby systems. Free shipping & manufacturer warranty on every order.",
    },
    solar: {
      title: "Solar Generators & Battery Backup Systems | Iron Haven Supply",
      desc: "Shop portable solar generators and battery backup stations from EcoFlow, Jackery, Bluetti & more. Silent, clean, emission-free. Free shipping included.",
    },
    gas: {
      title: "Gas Generators & Portable Power | Iron Haven Supply",
      desc: "Shop gas-powered generators from Honda, Generac, Cummins & more. High-wattage solutions for whole-home and job-site power. Free shipping on all orders.",
    },
    "whole-home": {
      title: "Whole Home Standby Generator Systems | Iron Haven Supply",
      desc: "Shop automatic whole-home standby generators that kick on in seconds. Generac, Cummins, Kohler & more. Full manufacturer warranties included.",
    },
  };
  const { title: shopTitle, desc: shopDesc } = categoryMeta[category] ?? categoryMeta.all;
  const shopCanonical = category === "all"
    ? "https://ironhavensupply.com/shop"
    : `https://ironhavensupply.com/shop?category=${category}`;

  return (
    <main className="font-sans bg-white min-h-screen">
      {/* ── Dynamic head tags ── */}
      <title>{shopTitle}</title>
      <meta name="description" content={shopDesc} />
      <link rel="canonical" href={shopCanonical} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={shopTitle} />
      <meta property="og:description" content={shopDesc} />
      <meta property="og:url" content={shopCanonical} />
      <meta property="og:site_name" content="Iron Haven Supply" />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={shopTitle} />
      <meta name="twitter:description" content={shopDesc} />

      {/* FAQPage structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      {/* BreadcrumbList structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <Navbar />

      {/* Top utility bar */}
      <div className="bg-gray-900 pt-16 hidden md:block">
        <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between">
          <div className="flex items-center gap-6 text-xs text-gray-300">
            <a href="tel:8666054975" className="flex items-center gap-1.5 hover:text-amber-400 transition-colors cursor-pointer">
              <i className="ri-phone-line text-amber-400"></i>
              (866) 605-4975
            </a>
            <span className="flex items-center gap-1.5">
              <i className="ri-price-tag-3-line text-amber-400"></i>
              Price Match Guarantee
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-question-line text-amber-400"></i>
              FAQs
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-star-line text-amber-400"></i>
              Customer Reviews
            </span>
          </div>
          <div className="flex items-center gap-6 text-xs text-gray-300">
            <span className="flex items-center gap-1.5">
              <i className="ri-truck-line text-amber-400"></i>
              Free Shipping on All Orders
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-shield-check-line text-amber-400"></i>
              Manufacturer Warranty
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-arrow-go-back-line text-amber-400"></i>
              30-Day Returns
            </span>
          </div>
        </div>
      </div>
      <div className="block md:hidden pt-20"></div>

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-4 pb-2">
        <nav className="flex items-center gap-1.5 text-xs text-gray-400">
          <Link to="/" className="hover:text-amber-500 transition-colors cursor-pointer">Home</Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <span className="text-gray-700 font-medium">Shop Backup Power</span>
        </nav>
      </div>

      {/* Collection header */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 pb-4 pt-1">
        <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-2">
          Backup Power Systems &amp; Solar Generators
        </h1>
        <p className="text-sm text-gray-500 max-w-4xl leading-relaxed">
          Shop our curated collection of solar generators, portable power stations, gas generators, and whole-home standby systems. Every unit is hand-selected for real-world reliability — top brands like EcoFlow, Generac, Jackery, Bluetti, Goal Zero, Honda, Anker, and more. Whether you need a compact station for emergencies or a 22kW whole-home backup system, we have the right solution for your needs and budget — all with free shipping and manufacturer warranties included.
        </p>
      </div>

      {/* Brand quick-links */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 pb-6">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-400 font-semibold mr-1 whitespace-nowrap">Shop by Brand:</span>
          {brandPills.map((b) => (
            <button
              key={b.name}
              onClick={() => handleBrandQuickFilter(b.name)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                selectedBrands.includes(b.name)
                  ? "bg-amber-500 border-amber-500 text-white"
                  : "bg-white border-gray-200 text-gray-600 hover:border-amber-400 hover:text-amber-600"
              }`}
            >
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className={b.icon}></i>
              </div>
              {b.name}
            </button>
          ))}
          {selectedBrands.length > 0 && (
            <button
              onClick={() => { setSelectedBrands([]); setPage(1); }}
              className="text-xs text-gray-400 hover:text-red-500 px-2 py-1.5 cursor-pointer transition-colors whitespace-nowrap"
            >
              <i className="ri-close-line"></i> Clear
            </button>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 pb-16">
        <div className="flex flex-col lg:flex-row gap-8 items-start">

          {/* Sidebar */}
          <ShopSidebar
            activeCategory={category}
            onCategoryChange={handleCategory}
            showInStock={showInStock}
            showOutOfStock={showOutOfStock}
            onAvailabilityChange={handleAvailability}
            priceBucket={priceBucket}
            onPriceBucketChange={handlePriceBucket}
            selectedBrands={selectedBrands}
            onBrandsChange={handleBrands}
            inStockCount={inStockCount}
            outOfStockCount={outOfStockCount}
          />

          {/* Products area */}
          <div className="flex-1 min-w-0">
            <ShopFilters
              activeSort={sort}
              onSortChange={handleSort}
              resultCount={filtered.length}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
            />

            {/* Search results strip */}
            {searchQuery.trim().length > 0 && (
              <div className="mb-4 flex items-center justify-between gap-3 px-4 py-3 bg-amber-50 border border-amber-100 rounded-lg">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-7 h-7 flex items-center justify-center bg-amber-500 rounded-md shrink-0">
                    <i className="ri-search-line text-white text-sm"></i>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-gray-900 truncate">
                      {filtered.length === 0
                        ? `No results for "${searchQuery}"`
                        : `${filtered.length} result${filtered.length !== 1 ? "s" : ""} for "${searchQuery}"`}
                    </p>
                    {filtered.length === 0 && (
                      <p className="text-xs text-gray-500 mt-0.5">Try a different search term or clear your filters</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => { setSearchQuery(""); setPage(1); }}
                  className="flex items-center gap-1.5 text-xs text-amber-700 hover:text-amber-900 font-semibold border border-amber-200 bg-white px-3 py-1.5 rounded-md cursor-pointer transition-colors whitespace-nowrap shrink-0"
                >
                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-close-line"></i>
                  </div>
                  Clear search
                </button>
              </div>
            )}

            {/* Active deal mode strip */}
            {(sort === "sale" || sort === "limited-stock") && (
              <div className={`mb-4 flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg text-sm font-semibold ${
                sort === "sale"
                  ? "bg-red-500 text-white"
                  : "bg-orange-500 text-white"
              }`}>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={sort === "sale" ? "ri-price-tag-3-fill" : "ri-alarm-warning-fill"}></i>
                  </div>
                  <span>
                    {sort === "sale"
                      ? `Showing ${filtered.length} sale item${filtered.length !== 1 ? "s" : ""}`
                      : `Showing ${filtered.length} item${filtered.length !== 1 ? "s" : ""} — limited stock first`}
                  </span>
                </div>
                <button
                  onClick={() => handleSort("featured")}
                  className="flex items-center gap-1.5 text-white/80 hover:text-white text-xs font-medium cursor-pointer transition-colors whitespace-nowrap"
                >
                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-close-line"></i>
                  </div>
                  Show all
                </button>
              </div>
            )}

            {/* On Sale callout banner */}
            {saleProducts.length > 0 && sort !== "sale" && (
              <OnSaleBanner
                count={saleProducts.length}
                maxDiscount={biggestDiscount}
                onShowDeals={() => handleSort("sale")}
              />
            )}

            {/* Limited Stock urgency banner */}
            {limitedStockCount > 0 && sort !== "limited-stock" && (
              <LimitedStockBanner
                count={limitedStockCount}
                onShowLimited={() => handleSort("limited-stock")}
              />
            )}

            {/* Loading state */}
            {loading && (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {Array.from({ length: 12 }).map((_, i) => (
                  <div key={i} className="bg-gray-50 border border-gray-100 rounded-lg overflow-hidden animate-pulse">
                    <div className="w-full h-52 bg-gray-200"></div>
                    <div className="p-4 space-y-2">
                      <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                      <div className="h-4 bg-gray-200 rounded w-full"></div>
                      <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                      <div className="h-8 bg-gray-200 rounded w-full mt-3"></div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Error state */}
            {error && !loading && (
              <div className="text-center py-16 text-red-500">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-error-warning-line text-4xl"></i>
                </div>
                <p className="font-semibold">Failed to load products</p>
                <p className="text-sm text-gray-400 mt-1">{error}</p>
              </div>
            )}

            {!loading && !error && paginated.length === 0 && (
              <div className="text-center py-24 text-gray-400">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 text-gray-300">
                  <i className="ri-inbox-line text-5xl"></i>
                </div>
                <p className="font-semibold text-gray-500 mb-1">No products match your filters.</p>
                <p className="text-sm">Try adjusting your filters or <button onClick={() => { setCategory("all"); setSelectedBrands([]); setPriceBucket("all"); setShowInStock(true); setShowOutOfStock(true); setPage(1); }} className="text-amber-500 underline cursor-pointer">clear all filters</button>.</p>
              </div>
            )}

            {!loading && !error && paginated.length > 0 && (
              viewMode === "grid" ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                    {paginated.slice(0, 6).map((p) => (
                      <ProductCard key={p.id} product={p} viewMode="grid" />
                    ))}
                  </div>

                  {paginated.length > 6 && (
                    <div className="my-6 bg-gray-900 rounded-lg px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center bg-amber-500/20 rounded-lg shrink-0">
                          <i className="ri-bank-card-line text-amber-400 text-xl"></i>
                        </div>
                        <div>
                          <p className="text-white font-bold text-sm">Financing Available — 0% APR</p>
                          <p className="text-gray-400 text-xs">Buy now, pay over time with approved credit. No fees, no surprises.</p>
                        </div>
                      </div>
                      <a
                        href="#financing"
                        className="bg-amber-500 hover:bg-amber-400 text-white text-xs font-bold px-5 py-2.5 rounded-md transition-colors whitespace-nowrap cursor-pointer"
                      >
                        Learn More
                      </a>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                    {paginated.slice(6).map((p) => (
                      <ProductCard key={p.id} product={p} viewMode="grid" />
                    ))}
                  </div>
                </>
              ) : (
                <div className="flex flex-col gap-4">
                  {paginated.map((p) => (
                    <ProductCard key={p.id} product={p} viewMode="list" />
                  ))}
                </div>
              )
            )}

            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />

            {/* Clearance Corner — always visible below main grid */}
            {!loading && !error && (
              <ClearanceCorner products={products} />
            )}
          </div>
        </div>

        {/* Trust strip */}
        <div className="mt-16 border border-gray-100 rounded-lg p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: "ri-truck-line", title: "Free Shipping", desc: "On every order, no minimums" },
            { icon: "ri-shield-check-line", title: "Warranty Included", desc: "Full manufacturer coverage" },
            { icon: "ri-customer-service-2-line", title: "Expert Support", desc: "(866) 605-4975 Mon\u2013Fri 9\u20136 EST" },
            { icon: "ri-bank-card-line", title: "Financing Available", desc: "0% APR on qualifying orders" },
          ].map((item) => (
            <div key={item.title} className="flex items-start gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-amber-50 rounded-lg shrink-0">
                <i className={`${item.icon} text-amber-500 text-lg`}></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">{item.title}</p>
                <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Educational content below grid */}
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <h2 className="text-xl font-extrabold text-gray-900 mb-4">Why Buy Backup Power from Iron Haven?</h2>
            <div className="text-sm text-gray-600 space-y-3 leading-relaxed">
              <p>
                At Iron Haven Supply, we do the research so you don&apos;t have to. Every backup power system, solar generator, and whole-home standby unit in our catalog has been carefully vetted for real-world performance — not just impressive spec sheets. We carry only brands that back their products with solid warranties and proven track records in the field.
              </p>
              <p>
                Unlike big-box retailers, you won&apos;t find generic, unbranded hardware here. Our team of backup power specialists personally reviews each product before it earns a spot in our catalog. From compact Jackery and EcoFlow portable stations perfect for camping or emergency use, to Generac and Cummins whole-home standby systems that kick on automatically within seconds of a power outage — we stock the full spectrum.
              </p>
              <p>
                Every order ships free. Every unit comes with full manufacturer warranty. And if you have questions before or after your purchase, our U.S.-based experts are available by phone, Monday through Friday, to help you make the right call.
              </p>
            </div>

            <div className="mt-8">
              <h3 className="text-base font-bold text-gray-900 mb-4">Frequently Asked Questions</h3>
              <div className="space-y-4">
                {[
                  {
                    q: "What\u2019s the difference between a solar generator and a gas generator?",
                    a: "Solar generators (portable power stations) use batteries charged by solar panels or wall outlets. They\u2019re silent, emission-free, and safe for indoor use. Gas generators produce more wattage but require ventilation, fuel storage, and regular maintenance.",
                  },
                  {
                    q: "How long will a backup power station power my home?",
                    a: "It depends on the unit\u2019s capacity and your load. A 2kWh station can run a refrigerator for ~24 hours, lights for 2\u20133 days, or a CPAP machine for multiple nights. Use our Power Calculator on the homepage to estimate your needs.",
                  },
                  {
                    q: "Do you offer financing?",
                    a: "Yes \u2014 we offer 0% APR financing on qualifying orders through our approved lending partners. Apply in minutes and get a same-day decision.",
                  },
                  {
                    q: "What warranty coverage is included?",
                    a: "All products include the full manufacturer warranty \u2014 typically 2\u20135 years for portable stations and up to 7 years for whole-home systems. We also assist with warranty claims on your behalf.",
                  },
                ].map((item, i) => (
                  <FaqItem key={i} question={item.q} answer={item.a} />
                ))}
              </div>
            </div>
          </div>

          {/* Right column */}
          <div className="space-y-5">
            <div className="border border-gray-100 rounded-lg p-5">
              <div className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-lg mb-3">
                <i className="ri-customer-service-2-line text-amber-500 text-xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 text-sm mb-1">Talk to a Power Expert</h4>
              <p className="text-xs text-gray-500 mb-4 leading-relaxed">
                Not sure what size or type you need? Our specialists will help you pick the perfect system for your home, budget, and power requirements.
              </p>
              <a
                href="tel:8666054975"
                className="flex items-center justify-center gap-2 w-full bg-amber-500 hover:bg-amber-400 text-white font-bold text-sm py-3 rounded-md transition-colors cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-line"></i>
                </div>
                (866) 605-4975
              </a>
              <p className="text-xs text-gray-400 text-center mt-2">Mon\u2013Fri, 9am\u20136pm EST</p>
            </div>

            <div className="border border-gray-100 rounded-lg p-5">
              <div className="w-10 h-10 flex items-center justify-center bg-emerald-50 rounded-lg mb-3">
                <i className="ri-calculator-line text-emerald-500 text-xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 text-sm mb-1">Free Power Calculator</h4>
              <p className="text-xs text-gray-500 mb-4 leading-relaxed">
                Answer 3 quick questions about your home and we&apos;ll recommend the right capacity and system type for your situation.
              </p>
              <Link
                to="/"
                className="flex items-center justify-center gap-2 w-full border border-gray-200 hover:border-amber-400 text-gray-700 hover:text-amber-600 font-semibold text-sm py-3 rounded-md transition-colors cursor-pointer"
              >
                Use the Calculator
              </Link>
            </div>

            <div className="bg-gray-900 rounded-lg p-5">
              <p className="text-white font-bold text-sm mb-1">Iron Haven Price Match</p>
              <p className="text-gray-400 text-xs mb-3 leading-relaxed">
                Found it cheaper somewhere else? We&apos;ll match any authorized dealer price, no questions asked.
              </p>
              <a
                href="tel:8666054975"
                className="text-amber-400 hover:text-amber-300 text-xs font-semibold cursor-pointer transition-colors"
              >
                Contact us to price match &rarr;
              </a>
            </div>
          </div>
        </div>
      </div>

      <RecentlyViewedStrip />
      <Footer />
      <CompareBar />
      <CompareModal />
    </main>
  );
}

function OnSaleBanner({
  count,
  maxDiscount,
  onShowDeals,
}: {
  count: number;
  maxDiscount: number;
  onShowDeals: () => void;
}) {
  return (
    <div className="mb-5 rounded-lg overflow-hidden border border-red-100 bg-gradient-to-r from-red-50 via-orange-50 to-amber-50 relative">
      {/* Animated accent line */}
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-red-400 via-orange-400 to-amber-400"></div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 px-5 py-4">
        <div className="flex items-start gap-4">
          {/* Icon block */}
          <div className="w-10 h-10 flex items-center justify-center bg-red-500 rounded-lg shrink-0 mt-0.5">
            <i className="ri-price-tag-3-fill text-white text-xl"></i>
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-extrabold text-gray-900">
                {count} Products Currently On Sale
              </p>
              {maxDiscount > 0 && (
                <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap">
                  Up to {maxDiscount}% OFF
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
              Limited-time discounts on top backup power brands — EcoFlow, Jackery, Bluetti &amp; more.{" "}
              <span className="text-red-500 font-semibold">Prices can change anytime.</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0 sm:ml-4 w-full sm:w-auto">
          <button
            onClick={onShowDeals}
            className="flex items-center justify-center gap-2 bg-red-500 hover:bg-red-400 text-white text-xs font-bold px-5 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap w-full sm:w-auto"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-price-tag-3-line"></i>
            </div>
            Show Sale Items
          </button>
        </div>
      </div>
    </div>
  );
}

function LimitedStockBanner({
  count,
  onShowLimited,
}: {
  count: number;
  onShowLimited: () => void;
}) {
  return (
    <div className="mb-5 rounded-lg overflow-hidden border border-orange-100 bg-gradient-to-r from-orange-50 via-amber-50 to-yellow-50 relative">
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400"></div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 px-5 py-4">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 flex items-center justify-center bg-orange-500 rounded-lg shrink-0 mt-0.5">
            <i className="ri-alarm-warning-fill text-white text-xl"></i>
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-extrabold text-gray-900">
                {count} Item{count !== 1 ? "s" : ""} Running Low on Stock
              </p>
              <span className="bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap animate-pulse">
                Low Inventory
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
              These units are nearly sold out. Once they&apos;re gone, restock timelines vary by manufacturer.{" "}
              <span className="text-orange-600 font-semibold">Don&apos;t wait too long.</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0 sm:ml-4 w-full sm:w-auto">
          <button
            onClick={onShowLimited}
            className="flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-400 text-white text-xs font-bold px-5 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap w-full sm:w-auto"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-alarm-warning-line"></i>
            </div>
            Show Limited Stock
          </button>
        </div>
      </div>
    </div>
  );
}

function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-100 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left cursor-pointer hover:bg-gray-50 transition-colors"
      >
        <span className="text-sm font-semibold text-gray-800">{question}</span>
        <div className="w-5 h-5 flex items-center justify-center text-gray-400 shrink-0 ml-2">
          <i className={open ? "ri-subtract-line" : "ri-add-line"}></i>
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 text-sm text-gray-500 leading-relaxed border-t border-gray-100 pt-3">
          {answer}
        </div>
      )}
    </div>
  );
}
